import React from "react";
import { Link } from "gatsby";
import styled, { css } from "styled-components";
import SectionTitle from "../title";
import Subscribe from "../../pages/index/subscribe";
import config from "../../../data/config";

const ContainerStyle = css`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionContainer = styled.section`
  margin: 80px 0;
`;

const FooterWrapper = styled.footer`
  background-color: var(--black);
`;

const FooterContainer = styled.div`
  --flexDirection: column;
  ${ContainerStyle}
  display: flex;
  flex-direction: var(--flexDirection);
  flex-wrap: wrap;
  gap: 13px;
  justify-content: center;
  padding: 80px 10px;
  color: var(--white);
  @media (min-width: 750px) {
    --flexDirection: row;
  }
`;

const About = styled.div`
  h2 {
    margin: 0;
    font-weight: 700;
    font-size: 2.25rem;
    line-height: 27px;
    text-transform: uppercase;
  }
  p {
    font-size: 0.8125rem;
    line-height: 15px;
    text-transform: uppercase;
    margin: 30px 0 20px;
  }
  ul {
    list-style: none;
    display: flex;
    align-items: center;
    gap: 20px;
    margin: 0;
    padding: 0;
  }
  @media (min-width: 750px) {
    flex: 304px 0 0;
  }
`;

const FooterLinksWrapper = styled.div`
  h3 {
    margin: 13px 0 30px;
    font-weight: 700;
    font-size: 1.125rem;
    line-height: 13px;
    text-transform: uppercase;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0 0 0 28px;
    li:not(:first-child) {
      margin-top: 15px;
    }
    a {
      color: var(--white);
      font-size: 0.875rem;
      line-height: 10px;
      text-transform: capitalize;
    }
  }
  @media (min-width: 750px) {
    flex: 304px 0 0;
  }
`;

const FootBanner = styled.div`
  --justifyContent: center;
  --display: none;
  --gap: 5px;
  background-color: #101010;
  height: 40px;
  div {
    ${ContainerStyle}
    display: flex;
    align-items: center;
    justify-content: var(--justifyContent);
    height: 100%;
    p {
      display: var(--display);
      color: var(--white);
      margin: 0;
      font-size: 0.875rem;
      line-height: 10px;
      text-transform: capitalize;
    }
    ul {
      display: flex;
      gap: var(--gap);
      list-style: none;
      margin: 0;
      padding: 0;
      li {
        display: flex;
        align-items: center;
      }
    }
  }
  @media (min-width: 750px) {
    --justifyContent: space-between;
    --display: block;
    --gap: 10px;
  }
`;

interface FooterLinksProps {
  LinksTitle: string;
  Links: { name: string; url: string }[];
}

const Footer = () => {
  const firstMenu = [
    { name: "shirts", url: "#" },
    { name: "bottoms", url: "#" },
    { name: "polos", url: "#" },
    { name: "sweatshirts", url: "#" },
    { name: "accessories", url: "#" },
    { name: "gift cards", url: "#" },
  ];
  const secondMenu = [
    { name: "our story", url: "#" },
    { name: "our products", url: "#" },
    { name: "privacy policy", url: "#" },
    { name: "terms of service", url: "#" },
  ];
  const thirdMenu = [
    { name: "contact us", url: "#" },
    { name: "faq", url: "#" },
    { name: "returns & exchanges", url: "#" },
    { name: "holiday shipping", url: "#" },
    { name: "lifetime guarantee", url: "#" },
    { name: "size guide", url: "#" },
  ];

  return (
    <>
      <SectionContainer>
        <SectionTitle title="Subscribe for free shipping on your first order" fontSize="1.125rem" />
        <Subscribe />
      </SectionContainer>
      <FooterWrapper>
        <FooterContainer>
          <About>
            <h2>about</h2>
            <p>
              We're here to prove that fashion can be a force for good. Through our commitment to sustainable materials, ethical
              manufacturing, and a zero-plastic policy, we're always searching for better ways to put the planet first.
            </p>
            <ul>
              <li>
                <a href="https://www.facebook.com/" title="Facebook" aria-label="Facebook" rel="noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M24 12C24 5.37097 18.629 0 12 0C5.37097 0 0 5.37097 0 12C0 17.9894 4.38823 22.9539 10.125 23.8548V15.4689H7.07661V12H10.125V9.35613C10.125 6.34887 11.9153 4.68774 14.6574 4.68774C15.9706 4.68774 17.3439 4.92194 17.3439 4.92194V7.87355H15.8303C14.34 7.87355 13.875 8.79871 13.875 9.74758V12H17.2031L16.6708 15.4689H13.875V23.8548C19.6118 22.9539 24 17.9894 24 12Z"
                      fill="white"
                    />
                  </svg>
                </a>
              </li>
              <li>
                <a href="https://www.instagram.com/" title="Instagram" aria-label="Instagram" rel="noreferrer">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12.0027 5.8454C8.59743 5.8454 5.85075 8.59208 5.85075 11.9973C5.85075 15.4026 8.59743 18.1492 12.0027 18.1492C15.4079 18.1492 18.1546 15.4026 18.1546 11.9973C18.1546 8.59208 15.4079 5.8454 12.0027 5.8454ZM12.0027 15.9969C9.80212 15.9969 8.00312 14.2032 8.00312 11.9973C8.00312 9.79141 9.79677 7.99777 12.0027 7.99777C14.2086 7.99777 16.0022 9.79141 16.0022 11.9973C16.0022 14.2032 14.2032 15.9969 12.0027 15.9969ZM19.8412 5.59375C19.8412 6.39152 19.1987 7.02867 18.4062 7.02867C17.6085 7.02867 16.9713 6.38617 16.9713 5.59375C16.9713 4.80134 17.6138 4.15884 18.4062 4.15884C19.1987 4.15884 19.8412 4.80134 19.8412 5.59375ZM23.9157 7.05008C23.8247 5.12794 23.3856 3.42532 21.9775 2.02253C20.5747 0.619743 18.8721 0.180703 16.9499 0.0843279C14.9689 -0.0281093 9.03112 -0.0281093 7.05008 0.0843279C5.1333 0.175349 3.43068 0.614389 2.02253 2.01718C0.614389 3.41997 0.180703 5.12259 0.0843279 7.04473C-0.0281093 9.02577 -0.0281093 14.9635 0.0843279 16.9446C0.175349 18.8667 0.614389 20.5693 2.02253 21.9721C3.43068 23.3749 5.12794 23.8139 7.05008 23.9103C9.03112 24.0228 14.9689 24.0228 16.9499 23.9103C18.8721 23.8193 20.5747 23.3803 21.9775 21.9721C23.3803 20.5693 23.8193 18.8667 23.9157 16.9446C24.0281 14.9635 24.0281 9.03112 23.9157 7.05008ZM21.3564 19.0702C20.9388 20.1196 20.1303 20.9281 19.0755 21.351C17.496 21.9775 13.7481 21.8329 12.0027 21.8329C10.2572 21.8329 6.50396 21.9721 4.92984 21.351C3.88042 20.9334 3.07195 20.1249 2.64897 19.0702C2.02253 17.4907 2.16709 13.7428 2.16709 11.9973C2.16709 10.2519 2.02789 6.49861 2.64897 4.92448C3.06659 3.87507 3.87507 3.06659 4.92984 2.64361C6.50931 2.01718 10.2572 2.16174 12.0027 2.16174C13.7481 2.16174 17.5014 2.02253 19.0755 2.64361C20.1249 3.06124 20.9334 3.86972 21.3564 4.92448C21.9828 6.50396 21.8383 10.2519 21.8383 11.9973C21.8383 13.7428 21.9828 17.496 21.3564 19.0702Z"
                      fill="white"
                    />
                  </svg>
                </a>
              </li>
              <li>
                <a href="https://twitter.com/" title="Twitter" aria-label="Twitter" rel="noreferrer">
                  <svg width="30" height="24" viewBox="0 0 30 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M26.5125 5.98123C26.5313 6.24372 26.5313 6.50626 26.5313 6.76875C26.5313 14.775 20.4375 24 9.30001 24C5.86874 24 2.68126 23.0062 0 21.2813C0.487518 21.3375 0.956221 21.3563 1.4625 21.3563C4.2937 21.3563 6.89999 20.4 8.98125 18.7688C6.31874 18.7125 4.08749 16.9688 3.31872 14.5687C3.69375 14.625 4.06873 14.6625 4.46252 14.6625C5.00625 14.6625 5.55004 14.5874 6.05626 14.4563C3.28127 13.8937 1.19995 11.4562 1.19995 8.51249V8.43752C2.00617 8.88752 2.94375 9.16877 3.93743 9.20622C2.30617 8.1187 1.23747 6.26247 1.23747 4.16246C1.23747 3.03748 1.53741 2.00623 2.06244 1.10622C5.04371 4.78122 9.52498 7.18118 14.5499 7.44372C14.4562 6.99372 14.3999 6.52502 14.3999 6.05626C14.3999 2.71872 17.0999 0 20.4562 0C22.1999 0 23.7749 0.731248 24.8812 1.9125C26.2499 1.65001 27.5624 1.14374 28.7249 0.450003C28.2749 1.85628 27.3187 3.03754 26.0624 3.78748C27.2812 3.6563 28.4625 3.31872 29.5499 2.85002C28.7251 4.04997 27.6938 5.11868 26.5125 5.98123Z"
                      fill="white"
                    />
                  </svg>
                </a>
              </li>
            </ul>
          </About>
          <FooterLinks LinksTitle="shop cuts" Links={firstMenu} />
          <FooterLinks LinksTitle="company" Links={secondMenu} />
          <FooterLinks LinksTitle="support" Links={thirdMenu} />
        </FooterContainer>
        <FootBanner>
          <div>
            <p>{`©${new Date().getFullYear()} ${config.title}`}</p>
            <ul>
              <li>
                <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    opacity="0.07"
                    d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z"
                    fill="black"
                  />
                  <path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="white" />
                  <path
                    d="M28.3 10.1H28C27.6 11.1 27.3 11.6 27 13.1H28.9C28.6 11.6 28.6 10.9 28.3 10.1ZM31.2 16H29.5C29.4 16 29.4 16 29.3 15.9L29.1 15L29 14.8H26.6C26.5 14.8 26.4 14.8 26.4 15L26.1 15.9C26.1 16 26 16 26 16H23.9L24.1 15.5L27 8.7C27 8.2 27.3 8 27.8 8H29.3C29.4 8 29.5 8 29.5 8.2L30.9 14.7C31 15.1 31.1 15.4 31.1 15.8C31.2 15.9 31.2 15.9 31.2 16ZM17.8 15.7L18.2 13.9C18.3 13.9 18.4 14 18.4 14C19.1 14.3 19.8 14.5 20.5 14.4C20.7 14.4 21 14.3 21.2 14.2C21.7 14 21.7 13.5 21.3 13.1C21.1 12.9 20.8 12.8 20.5 12.6C20.1 12.4 19.7 12.2 19.4 11.9C18.2 10.9 18.6 9.5 19.3 8.8C19.9 8.4 20.2 8 21 8C22.2 8 23.5 8 24.1 8.2H24.2C24.1 8.8 24 9.3 23.8 9.9C23.3 9.7 22.8 9.5 22.3 9.5C22 9.5 21.7 9.5 21.4 9.6C21.2 9.6 21.1 9.7 21 9.8C20.8 10 20.8 10.3 21 10.5L21.5 10.9C21.9 11.1 22.3 11.3 22.6 11.5C23.1 11.8 23.6 12.3 23.7 12.9C23.9 13.8 23.6 14.6 22.8 15.2C22.3 15.6 22.1 15.8 21.4 15.8C20 15.8 18.9 15.9 18 15.6C17.9 15.8 17.9 15.8 17.8 15.7ZM14.3 16C14.4 15.3 14.4 15.3 14.5 15C15 12.8 15.5 10.5 15.9 8.3C16 8.1 16 8 16.2 8H18C17.8 9.2 17.6 10.1 17.3 11.2C17 12.7 16.7 14.2 16.3 15.7C16.3 15.9 16.2 15.9 16 15.9L14.3 16ZM5 8.2C5 8.1 5.2 8 5.3 8H8.7C9.2 8 9.6 8.3 9.7 8.8L10.6 13.2C10.6 13.3 10.6 13.3 10.7 13.4C10.7 13.3 10.8 13.3 10.8 13.3L12.9 8.2C12.8 8.1 12.9 8 13 8H15.1C15.1 8.1 15.1 8.1 15 8.2L11.9 15.5C11.8 15.7 11.8 15.8 11.7 15.9C11.6 16 11.4 15.9 11.2 15.9H9.7C9.6 15.9 9.5 15.9 9.5 15.7L7.9 9.5C7.7 9.3 7.4 9 7 8.9C6.4 8.6 5.3 8.4 5.1 8.4L5 8.2Z"
                    fill="#142688"
                  />
                </svg>
              </li>
              <li>
                <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    opacity="0.07"
                    d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z"
                    fill="black"
                  />
                  <path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="white" />
                  <path
                    d="M15 19C18.866 19 22 15.866 22 12C22 8.13401 18.866 5 15 5C11.134 5 8 8.13401 8 12C8 15.866 11.134 19 15 19Z"
                    fill="#EB001B"
                  />
                  <path
                    d="M23 19C26.866 19 30 15.866 30 12C30 8.13401 26.866 5 23 5C19.134 5 16 8.13401 16 12C16 15.866 19.134 19 23 19Z"
                    fill="#F79E1B"
                  />
                  <path
                    d="M22 12C22 9.60005 20.8 7.50005 19 6.30005C17.2 7.60005 16 9.70005 16 12C16 14.3 17.2 16.5 19 17.7C20.8 16.5 22 14.4 22 12Z"
                    fill="#FF5F00"
                  />
                </svg>
              </li>
              <li>
                <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    opacity="0.07"
                    d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z"
                    fill="black"
                  />
                  <path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="white" />
                  <path
                    d="M23.9 8.3C24.1 7.3 23.9 6.6 23.3 6C22.7 5.3 21.6 5 20.2 5H16.1C15.8 5 15.6 5.2 15.5 5.5L14 15.6C14 15.8 14.1 16 14.3 16H17L17.4 12.6L19.2 10.4L23.9 8.3Z"
                    fill="#003087"
                  />
                  <path
                    d="M23.9 8.30005L23.7 8.50005C23.2 11.3 21.5 12.3 19.1 12.3H18C17.7 12.3 17.5 12.5 17.4 12.8L16.8 16.7L16.6 17.7C16.6 17.9 16.7 18.1 16.9 18.1H19C19.3 18.1 19.5 17.9 19.5 17.7V17.6L19.9 15.2V15.1C19.9 14.9 20.2 14.7 20.4 14.7H20.7C22.8 14.7 24.4 13.9 24.8 11.5C25 10.5 24.9 9.70005 24.4 9.10005C24.3 8.60005 24.1 8.40005 23.9 8.30005Z"
                    fill="#3086C8"
                  />
                  <path
                    d="M23.3 8.10005C23.2 8.00005 23.1 8.00005 23 8.00005C22.9 8.00005 22.8 8.00005 22.7 7.90005C22.4 7.80005 22 7.80005 21.6 7.80005H18.6C18.5 7.80005 18.4 7.80005 18.4 7.90005C18.2 8.00005 18.1 8.10005 18.1 8.30005L17.4 12.7V12.8C17.4 12.5 17.7 12.3 18 12.3H19.3C21.8 12.3 23.4 11.3 23.9 8.50005V8.30005C23.8 8.20005 23.6 8.10005 23.4 8.10005H23.3Z"
                    fill="#012169"
                  />
                </svg>
              </li>
              <li>
                <svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.18182 0H33.8182C34.9091 0 36 1.09091 36 2.18182V21.8182C36 22.9091 34.9091 24 33.8182 24H2.18182C1.09091 24 0 22.9091 0 21.8182V2.18182C0 1.09091 1.09091 0 2.18182 0Z"
                    fill="#D1D1D1"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M2.18182 1.09082H33.8182C34.3636 1.09082 34.9091 1.63627 34.9091 2.18173V21.8181C34.9091 22.3635 34.3636 22.909 33.8182 22.909H2.18182C1.63637 22.909 1.09091 22.3635 1.09091 21.8181V2.18173C1.09091 1.63627 1.63637 1.09082 2.18182 1.09082Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M7.63635 22.909H33.8192C34.4189 22.909 34.9055 22.4278 34.9061 21.8281C34.9061 21.8281 34.9137 15.3443 34.905 13.0908C27.1045 19.6342 15.5297 22.909 7.63635 22.909Z"
                    fill="#E67730"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M33.021 11.1833C33.021 10.3326 32.3916 9.8418 31.2938 9.8418H29.8821V14.3857H30.833V12.5603H30.9573L32.2749 14.3857H33.4456L31.9091 12.4713C32.6261 12.3356 33.021 11.879 33.021 11.1833ZM31.1111 11.9338H30.833V10.5575H31.1262C31.719 10.5575 32.0414 10.7889 32.0414 11.2311C32.0414 11.6879 31.719 11.9338 31.1111 11.9338Z"
                    fill="#1A1919"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M26.5519 14.3857H29.2518V13.6164H27.5032V12.3899H29.1873V11.6203H27.5032V10.6118H29.2518V9.8418H26.5519V14.3857Z"
                    fill="#1A1919"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M23.7712 12.8939L22.4698 9.8418H21.4297L23.501 14.5023H24.0131L26.1215 9.8418H25.0895L23.7712 12.8939Z"
                    fill="#1A1919"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21.7156 12.1168C21.7156 13.4511 20.5545 14.5327 19.1206 14.5327C17.6874 14.5327 16.5257 13.4511 16.5257 12.1168C16.5257 10.7822 17.6874 9.70068 19.1206 9.70068C20.5545 9.70068 21.7156 10.7822 21.7156 12.1168Z"
                    fill="#E67730"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.4537 12.1168C12.4537 13.4524 13.5801 14.488 15.0297 14.488C15.4394 14.488 15.7905 14.413 16.2232 14.2232V13.1799C15.8426 13.5345 15.5056 13.6776 15.074 13.6776C14.1152 13.6776 13.4347 13.0304 13.4347 12.1101C13.4347 11.2378 14.1367 10.5497 15.0297 10.5497C15.4837 10.5497 15.8275 10.7005 16.2232 11.0609V10.0181C15.8054 9.82082 15.4617 9.73901 15.0521 9.73901C13.6099 9.73901 12.4537 10.7956 12.4537 12.1168Z"
                    fill="#1A1919"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M10.7496 11.5855C10.1783 11.3887 10.0106 11.2588 10.0106 11.0133C10.0106 10.727 10.3095 10.5097 10.7198 10.5097C11.0051 10.5097 11.2394 10.6187 11.4874 10.8776L11.9855 10.2704C11.5762 9.93706 11.0865 9.7666 10.5514 9.7666C9.68784 9.7666 9.02922 10.3248 9.02922 11.0686C9.02922 11.6947 9.336 12.015 10.2299 12.3147C10.6027 12.437 10.7924 12.5187 10.8882 12.5734C11.0782 12.6891 11.1734 12.8527 11.1734 13.0436C11.1734 13.4117 10.8591 13.6845 10.4343 13.6845C9.98028 13.6845 9.61468 13.4731 9.39539 13.0785L8.78033 13.6299C9.21891 14.2292 9.74572 14.4948 10.4701 14.4948C11.4593 14.4948 12.1533 13.8825 12.1533 13.0028C12.1533 12.281 11.8324 11.9541 10.7496 11.5855Z"
                    fill="#1A1919"
                  />
                  <path fillRule="evenodd" clipRule="evenodd" d="M7.46991 9.8418H8.42191V14.3857H7.46991V9.8418Z" fill="#1A1919" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.41195 9.8418H3.01459V14.3857H4.40459C5.14372 14.3857 5.67732 14.2233 6.14587 13.8612C6.70267 13.4321 7.03188 12.7856 7.03188 12.1169C7.03188 10.7759 5.95581 9.8418 4.41195 9.8418ZM5.52403 13.2552C5.2248 13.5067 4.83638 13.6164 4.22132 13.6164H3.96583V10.6118H4.22132C4.83638 10.6118 5.20952 10.7141 5.52403 10.9792C5.85324 11.2522 6.05122 11.6751 6.05122 12.1102C6.05122 12.5465 5.85324 12.9824 5.52403 13.2552Z"
                    fill="#1A1919"
                  />
                </svg>
              </li>
              <li>
                <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    opacity="0.07"
                    d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z"
                    fill="black"
                  />
                  <path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="#006FCF" />
                  <path
                    d="M8.971 10.268L9.745 12.144H8.203L8.971 10.268ZM25.046 10.346H22.069V11.173H24.998V12.412H22.075V13.334H25.052V14.073L27.129 11.828L25.052 9.488L25.046 10.346ZM10.983 8.006H14.978L15.865 9.941L16.687 8H27.057L28.135 9.19L29.25 8H34.013L30.494 11.852L33.977 15.68H29.143L28.065 14.49L26.94 15.68H10.03L9.536 14.49H8.406L7.911 15.68H4L7.286 8H10.716L10.983 8.006ZM19.646 9.084H17.407L15.907 12.62L14.282 9.084H12.06V13.894L10 9.084H8.007L5.625 14.596H7.18L7.674 13.406H10.27L10.764 14.596H13.484V10.661L15.235 14.602H16.425L18.165 10.673V14.603H19.623L19.647 9.083L19.646 9.084ZM28.986 11.852L31.517 9.084H29.695L28.094 10.81L26.546 9.084H20.652V14.602H26.462L28.076 12.864L29.624 14.602H31.499L28.987 11.852H28.986Z"
                    fill="white"
                  />
                </svg>
              </li>
              <li>
                <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    opacity="0.07"
                    d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z"
                    fill="black"
                  />
                  <path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="white" />
                  <path
                    d="M18.093 11.976V15.176H17.075V7.27599H19.766C20.0884 7.27053 20.4088 7.32885 20.7086 7.44761C21.0084 7.56637 21.2818 7.74322 21.513 7.96799C21.732 8.17238 21.9087 8.41794 22.0327 8.69062C22.1568 8.96331 22.2259 9.25777 22.2362 9.55719C22.2464 9.8566 22.1975 10.1551 22.0923 10.4356C21.9871 10.7161 21.8276 10.9731 21.623 11.192L21.513 11.308C21.043 11.755 20.415 11.998 19.766 11.982L18.093 11.976ZM18.093 8.24399V11.032H19.791C20.168 11.044 20.532 10.897 20.796 10.628C20.9848 10.4312 21.1117 10.1832 21.1606 9.91482C21.2096 9.64649 21.1787 9.36967 21.0716 9.11881C20.9645 8.86795 20.786 8.65412 20.5583 8.50391C20.3306 8.3537 20.0638 8.27374 19.791 8.27399L18.093 8.24399ZM24.577 9.59199C25.227 9.56199 25.863 9.77999 26.355 10.205C26.8 10.635 27.037 11.235 27.005 11.854V15.188H26.036V14.422H25.987C25.8132 14.7093 25.5675 14.9463 25.2741 15.1096C24.9807 15.2728 24.6498 15.3567 24.314 15.353C23.7659 15.3707 23.2314 15.1803 22.818 14.82C22.6221 14.6602 22.4652 14.458 22.359 14.2286C22.2528 13.9992 22.2001 13.7487 22.205 13.496C22.1935 13.2406 22.2431 12.9861 22.3497 12.7537C22.4563 12.5214 22.6169 12.3178 22.818 12.16C23.3114 11.8051 23.9086 11.624 24.516 11.645C25.033 11.625 25.546 11.738 26.006 11.976V11.768C26.0094 11.596 25.9736 11.4255 25.9014 11.2694C25.8291 11.1133 25.7223 10.9757 25.589 10.867C25.3213 10.6236 24.9707 10.492 24.609 10.499C24.3463 10.4965 24.0874 10.561 23.8566 10.6865C23.6258 10.812 23.4308 10.9942 23.29 11.216L22.395 10.652C22.638 10.3038 22.9661 10.0236 23.3481 9.83804C23.73 9.65249 24.1531 9.56779 24.577 9.59199ZM23.29 13.52C23.2876 13.6495 23.3171 13.7776 23.3758 13.8931C23.4346 14.0085 23.5209 14.1077 23.627 14.182C23.85 14.358 24.127 14.451 24.412 14.445C24.841 14.444 25.252 14.275 25.558 13.973C25.863 13.687 26.036 13.288 26.036 12.87C25.651 12.5963 25.1833 12.4642 24.712 12.496C24.3461 12.4816 23.9852 12.5846 23.682 12.79C23.5619 12.8703 23.4634 12.9789 23.3951 13.1062C23.3267 13.2334 23.2907 13.3755 23.29 13.52ZM32.576 9.76999L29.186 17.56H28.138L29.419 14.832L27.195 9.76999H28.298L29.91 13.655L31.479 9.76999H32.576Z"
                    fill="#5F6368"
                  />
                  <path
                    d="M13.986 11.284C13.986 10.976 13.962 10.668 13.913 10.364H9.62299V12.111H12.074C12.0241 12.3887 11.9186 12.6534 11.764 12.8893C11.6094 13.1252 11.4087 13.3275 11.174 13.484V14.618H12.638C13.0831 14.1873 13.4331 13.6681 13.6652 13.0938C13.8974 12.5196 14.0067 11.9031 13.986 11.284Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9.62903 15.7209C10.7353 15.7513 11.8117 15.359 12.639 14.6239L11.173 13.4839C10.8208 13.7098 10.4217 13.8523 10.006 13.9008C9.59043 13.9492 9.16924 13.9022 8.77451 13.7633C8.37978 13.6245 8.02191 13.3975 7.72812 13.0995C7.43432 12.8016 7.21233 12.4406 7.07903 12.0439H5.57703V13.2139C5.95288 13.967 6.53107 14.6004 7.24678 15.0432C7.96249 15.486 8.78741 15.7207 9.62903 15.7209Z"
                    fill="#34A853"
                  />
                  <path
                    d="M7.07899 12.05C6.88879 11.4873 6.88879 10.8777 7.07899 10.315V9.14502H5.57699C5.25678 9.77647 5.0899 10.4745 5.0899 11.1825C5.0899 11.8905 5.25678 12.5886 5.57699 13.22L7.07899 12.05Z"
                    fill="#FBBC04"
                  />
                  <path
                    d="M9.62903 8.43994C10.276 8.42813 10.9015 8.67255 11.369 9.11994L12.669 7.82694C11.8401 7.04896 10.7407 6.6246 9.60403 6.64394C8.76739 6.64814 7.94827 6.88395 7.23746 7.32522C6.52665 7.7665 5.95194 8.396 5.57703 9.14394L7.07903 10.3149C7.25465 9.77503 7.59512 9.30375 8.05253 8.96741C8.50995 8.63108 9.0613 8.4466 9.62903 8.43994Z"
                    fill="#EA4335"
                  />
                </svg>
              </li>
              <li>
                <svg width="38" height="24" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M34.3872 0H3.61287C3.48468 0 3.35626 0 3.2283 0.00067947C3.12026 0.00158543 3.01245 0.00271788 2.90441 0.00566225C2.66909 0.012004 2.43173 0.0260463 2.19935 0.0677205C1.9663 0.107648 1.74051 0.182074 1.52939 0.288548C1.10499 0.504411 0.760026 0.849374 0.544163 1.27378C0.4375 1.48497 0.363065 1.71095 0.323336 1.94419C0.281209 2.17679 0.267393 2.41393 0.261051 2.64903C0.258291 2.75697 0.25663 2.86493 0.256068 2.97291C0.255615 3.10065 0.255615 3.22884 0.255615 3.35726V20.6425C0.255615 20.7709 0.255615 20.8989 0.256068 21.0273C0.256748 21.1351 0.258107 21.2432 0.261051 21.3512C0.267393 21.5858 0.281435 21.8232 0.323336 22.0556C0.362983 22.2888 0.437342 22.5148 0.543937 22.726C0.651108 22.9369 0.790999 23.1296 0.958413 23.2967C1.12554 23.4642 1.31819 23.6041 1.52917 23.7112C1.74365 23.8206 1.96335 23.8902 2.19958 23.9325C2.43173 23.974 2.66909 23.988 2.90441 23.9943C3.01245 23.9968 3.12026 23.9982 3.2283 23.9989C3.35626 23.9998 3.48468 23.9998 3.61287 23.9998H34.3872C34.5152 23.9998 34.6436 23.9998 34.7715 23.9989C34.8794 23.9984 34.9872 23.9968 35.0956 23.9943C35.3305 23.988 35.5679 23.974 35.8007 23.9325C36.0338 23.8925 36.2596 23.818 36.4707 23.7112C36.8949 23.4951 37.2397 23.1502 37.4559 22.726C37.5653 22.5115 37.6346 22.2918 37.6765 22.0556C37.7186 21.8232 37.7322 21.5858 37.7386 21.3512C37.7415 21.2432 37.7431 21.1351 37.7435 21.0273C37.7444 20.8989 37.7444 20.7709 37.7444 20.6425V3.35749C37.7444 3.22907 37.7444 3.10087 37.7435 2.97268C37.7431 2.86478 37.7414 2.75689 37.7386 2.64903C37.7322 2.41393 37.7186 2.17679 37.6765 1.94419C37.6371 1.71091 37.5627 1.4849 37.4559 1.27378C37.2399 0.849448 36.895 0.504513 36.4707 0.288548C36.2595 0.182184 36.0337 0.107836 35.8007 0.067947C35.5679 0.0260463 35.3305 0.0122305 35.0956 0.00588874C34.9876 0.00314533 34.8796 0.00148432 34.7715 0.000905959C34.6436 0 34.5152 0 34.3872 0Z"
                    fill="black"
                  />
                  <path
                    d="M34.3871 0.800049L34.7658 0.800728C34.8682 0.801408 34.9708 0.80254 35.0739 0.805258C35.2535 0.810241 35.4632 0.81998 35.6589 0.855086C35.8288 0.885662 35.9714 0.932092 36.1082 1.00185C36.382 1.14104 36.6044 1.36369 36.7433 1.63761C36.8126 1.7735 36.8588 1.91529 36.8894 2.08674C36.9243 2.28016 36.934 2.49057 36.9388 2.67108C36.9417 2.773 36.9431 2.87492 36.9433 2.97911C36.9444 3.10526 36.9444 3.23119 36.9444 3.35757V20.6428C36.9444 20.7692 36.9444 20.8951 36.9435 21.0238C36.9431 21.1257 36.9417 21.2276 36.939 21.3295C36.934 21.5098 36.9243 21.7202 36.889 21.9159C36.8633 22.0716 36.814 22.2224 36.7429 22.3632C36.6037 22.6373 36.3809 22.8599 36.1069 22.999C35.971 23.0683 35.8288 23.1147 35.6605 23.1451C35.4609 23.1806 35.2421 23.1904 35.0775 23.1949C34.974 23.1972 34.8709 23.1988 34.7652 23.1992C34.6395 23.2001 34.5131 23.2001 34.3871 23.2001H3.60786C3.48329 23.2001 3.35872 23.2001 3.23188 23.1992C3.12995 23.1988 3.02803 23.1974 2.92612 23.1951C2.75761 23.1906 2.53882 23.1806 2.34087 23.1453C2.18436 23.1192 2.03271 23.0696 1.89106 22.9981C1.7552 22.9291 1.63119 22.8389 1.5237 22.7308C1.41587 22.6232 1.32581 22.4991 1.25689 22.3632C1.18572 22.2216 1.13639 22.07 1.11058 21.9137C1.07525 21.7182 1.06528 21.5087 1.06075 21.3298C1.05819 21.2275 1.05668 21.1252 1.05622 21.0229L1.05554 20.7223V3.2783L1.05599 2.9782C1.05646 2.87574 1.05797 2.77329 1.06052 2.67086C1.06551 2.49148 1.07525 2.28197 1.11081 2.08493C1.13666 1.92884 1.18599 1.77757 1.25712 1.63625C1.32597 1.50081 1.41566 1.37714 1.52438 1.26866C1.63213 1.16074 1.75627 1.07054 1.8922 1.0014C2.03349 0.930411 2.18467 0.881088 2.34065 0.855086C2.53656 0.81998 2.74652 0.810241 2.92635 0.805258C3.02872 0.80254 3.13132 0.801408 3.23302 0.800728L3.61284 0.800049H34.3871Z"
                    fill="white"
                  />
                  <path
                    d="M10.1097 8.10147C10.4277 7.70398 10.6433 7.17014 10.5865 6.62476C10.1213 6.64786 9.55369 6.93165 9.22483 7.32959C8.92971 7.67023 8.66857 8.22627 8.73652 8.74878C9.2588 8.79408 9.78064 8.48786 10.1097 8.10147Z"
                    fill="black"
                  />
                  <path
                    d="M10.5806 8.85094C9.82187 8.80564 9.17727 9.28127 8.81512 9.28127C8.45273 9.28127 7.89806 8.87359 7.29809 8.88469C6.51738 8.89601 5.79306 9.33767 5.39671 10.0398C4.58179 11.444 5.18154 13.5277 5.97425 14.6715C6.35906 15.2377 6.82291 15.8606 7.43398 15.8382C8.01153 15.8155 8.23779 15.4644 8.93946 15.4644C9.6409 15.4644 9.84474 15.8382 10.456 15.8268C11.0898 15.8155 11.4861 15.2606 11.8709 14.6944C12.3126 14.0489 12.4931 13.426 12.5044 13.3921C12.4931 13.3807 11.2823 12.9162 11.2709 11.5233C11.2596 10.3569 12.2218 9.80197 12.267 9.76777C11.7237 8.96419 10.8748 8.87359 10.5804 8.85094"
                    fill="black"
                  />
                  <path
                    d="M18.1422 7.27246C19.7906 7.27246 20.9386 8.40876 20.9386 10.063C20.9386 11.7232 19.767 12.8654 18.101 12.8654H16.2759V15.7681H14.9573V7.27246H18.1422ZM16.2759 11.7585H17.7889C18.9372 11.7585 19.5904 11.1405 19.5904 10.0689C19.5904 8.99763 18.9369 8.38521 17.7947 8.38521H16.2759V11.7585Z"
                    fill="black"
                  />
                  <path
                    d="M21.2657 14.0075C21.2657 12.9183 22.0958 12.2944 23.6264 12.2002L25.2689 12.1V11.6289C25.2689 10.9404 24.8157 10.5635 24.0092 10.5635C23.3437 10.5635 22.8609 10.9049 22.7612 11.4287H21.5717C21.6072 10.328 22.6432 9.52734 24.0445 9.52734C25.5516 9.52734 26.5347 10.3162 26.5347 11.5408V15.7678H25.3162V14.7493H25.2868C24.9394 15.4145 24.1738 15.8324 23.3437 15.8324C22.1193 15.8324 21.2657 15.1026 21.2657 14.0075ZM25.2689 13.4601V12.9831L23.8031 13.0773C22.9786 13.1303 22.5492 13.4363 22.5492 13.972C22.5492 14.4902 22.9965 14.8258 23.6971 14.8258C24.5917 14.8258 25.2689 14.2546 25.2689 13.4601Z"
                    fill="black"
                  />
                  <path
                    d="M27.6552 18.0403V17.022C27.7377 17.0336 27.9376 17.0453 28.0436 17.0453C28.6264 17.0453 28.9562 16.7985 29.1564 16.1622L29.2742 15.7856L27.0428 9.604H28.4203L29.9747 14.6198H30.0041L31.5583 9.604H32.9007L30.5869 16.0975C30.0569 17.5871 29.4506 18.0759 28.1673 18.0759C28.0672 18.0759 27.7433 18.0641 27.6552 18.0403Z"
                    fill="black"
                  />
                </svg>
              </li>
            </ul>
          </div>
        </FootBanner>
      </FooterWrapper>
    </>
  );
};

const FooterLinks = ({ LinksTitle, Links }: FooterLinksProps) => (
  <FooterLinksWrapper>
    <h3>{LinksTitle}</h3>
    <ul>
      {Links.map(({ name, url }, id) => (
        <li key={id}>
          <Link to={url}>{name}</Link>
        </li>
      ))}
    </ul>
  </FooterLinksWrapper>
);

export default Footer;
